import { useLayoutEffect, useRef } from 'react';
import { industries } from '../data';

export default function Industries() {
  const scroller = useRef(null);
  const cardRefs = useRef([]);
  const bodyRefs = useRef([]);
  const titleRefs = useRef([]);

  const scrollByCard = (direction) => {
    const node = scroller.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.round(node.clientWidth * 0.72), behavior: 'smooth' });
  };

  useLayoutEffect(() => {
    let frame = 0;

    const syncLayout = () => {
      const bodies = bodyRefs.current.filter(Boolean);
      const titles = titleRefs.current.filter(Boolean);
      if (!bodies.length || !titles.length) return;

      bodies.forEach((body) => {
        body.style.height = '';
      });
      titles.forEach((title) => {
        title.style.minHeight = '';
      });

      const maxTitleHeight = titles.reduce(
        (max, title) => Math.max(max, title.getBoundingClientRect().height),
        0
      );
      titles.forEach((title) => {
        title.style.minHeight = `${maxTitleHeight}px`;
      });

      const maxBodyHeight = bodies.reduce(
        (max, body) => Math.max(max, body.getBoundingClientRect().height),
        0
      );
      bodies.forEach((body) => {
        body.style.height = `${maxBodyHeight}px`;
      });
    };

    const scheduleSync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncLayout);
    };

    const cards = cardRefs.current.filter(Boolean);
    if (!cards.length) return undefined;

    window.addEventListener('resize', scheduleSync);
    scheduleSync();

    if (typeof ResizeObserver !== 'function') {
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('resize', scheduleSync);
        bodyRefs.current.filter(Boolean).forEach((body) => {
          body.style.height = '';
        });
        titleRefs.current.filter(Boolean).forEach((title) => {
          title.style.minHeight = '';
        });
      };
    }

    const ro = new ResizeObserver(scheduleSync);
    cards.forEach((card) => ro.observe(card));

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('resize', scheduleSync);
      bodyRefs.current.filter(Boolean).forEach((body) => {
        body.style.height = '';
      });
      titleRefs.current.filter(Boolean).forEach((title) => {
        title.style.minHeight = '';
      });
    };
  }, []);

  return (
    <section className="section industries" id="industries">
      <div className="container">
        <div className="section-head with-controls" data-reveal>
          <div>
            <h2 className="display-2">
              Built for <em>service businesses.</em>
            </h2>
            <p className="section-lede">
              Straightforward bookkeeping and payroll for businesses built around people, projects,
              and professional services.
            </p>
          </div>
          <div className="scroll-controls">
            <button type="button" onClick={() => scrollByCard(-1)} aria-label="Scroll cards left">
              <span aria-hidden="true">←</span>
            </button>
            <button type="button" onClick={() => scrollByCard(1)} aria-label="Scroll cards right">
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>

      <div
        className="industry-scroller"
        ref={scroller}
        tabIndex={0}
        role="group"
        aria-label="Industries we work with — scroll horizontally to see more"
      >
        {industries.map((industry, index) => (
          <article
            className="industry-card"
            key={industry.name}
            ref={(node) => {
              cardRefs.current[index] = node;
            }}
          >
            <img
              src={industry.image}
              alt=""
              loading="lazy"
              decoding="async"
            />
            <div
              className="industry-body"
              ref={(node) => {
                bodyRefs.current[index] = node;
              }}
            >
              <h3
                ref={(node) => {
                  titleRefs.current[index] = node;
                }}
              >
                {industry.name}
              </h3>
              <p className="industry-copy">{industry.description}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
